import Vue from 'vue';
import Router from 'vue-router';
import Home from './views/Home.vue';

import UserModule from '@/store/modules/User';
import store from '@/store';
import { getModule } from 'vuex-module-decorators';

Vue.use(Router);

const Session = () => import('./views/Session.vue');
const SessionList = () => import('./views/SessionList.vue');
const ConfirmEmail = () => import('./views/ConfirmEmail.vue');
const ResetPassword = () => import('./views/ResetPassword.vue');
const NotFound = () => import('./views/NotFound.vue');

const router = new Router({
    mode: 'history',
    base: process.env.BASE_URL,
    routes: [
        {
          path: '/',
          name: 'home',
          component: Home,
        },
        {
          path: '/sessions',
          name: 'sessionList',
          component: SessionList,
        },
        {
          path: '/sessions/new',
          name: 'newSession',
          component: Session,
        },
        {
          path: '/sessions/:id',
          name: 'editSession',
          component: Session,
          props: true,
        },
        {
          path: '/confirm-email/:code',
          name: 'confirmEmail',
          component: ConfirmEmail,
          props: true,
        },
        {
          path: '/recover-password/:code',
          name: 'recoverPassword',
          component: ResetPassword,
          props: true,
        },
        {
          path: '/terms',
          name: 'terms',
          component: Home,
        },
        {
          path: '/privacy',
          name: 'privacy',
          component: Home,
        },
        {
          path: '*',
          name: 'notFound',
          component: NotFound, // Catch-all route for 404
        },
    ],
});

interface SeoConfig {
    title: string;
    description: string;
    canonicalPath?: string;
    indexable: boolean;
    structuredData?: Record<string, unknown>;
}

const seoDefaults = {
    siteUrl: 'https://raws.app',
    title: 'RAWS - aplikacja dla fotografów do obsługi sesji i sprzedaży zdjęć',
    description: 'RAWS to aplikacja dla fotografów do obsługi sesji, galerii online, wyboru zdjęć, zamówień, cenników, pobrań i komunikacji z klientem.',
    shareImagePath: '/main/mocup_screens.png',
    shareImageAlt: 'RAWS - aplikacja dla fotografów do obsługi sesji i sprzedaży zdjęć',
    shareImageWidth: '900',
    shareImageHeight: '500',
};

const buildAbsoluteUrl = (path: string) => `${seoDefaults.siteUrl}${path}`;

const createGenericPageSchema = (title: string, description: string, path: string): Record<string, unknown> => ({
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'WebSite',
            '@id': `${seoDefaults.siteUrl}/#website`,
            name: 'RAWS',
            url: `${seoDefaults.siteUrl}/`,
            inLanguage: 'pl-PL',
        },
        {
            '@type': 'WebPage',
            '@id': `${buildAbsoluteUrl(path)}#webpage`,
            url: buildAbsoluteUrl(path),
            name: title,
            description,
            inLanguage: 'pl-PL',
            isPartOf: {
                '@id': `${seoDefaults.siteUrl}/#website`,
            },
        },
    ],
});

const createHomeSchema = (): Record<string, unknown> => ({
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'WebSite',
            '@id': `${seoDefaults.siteUrl}/#website`,
            name: 'RAWS',
            url: `${seoDefaults.siteUrl}/`,
            description: seoDefaults.description,
            inLanguage: 'pl-PL',
        },
        {
            '@type': 'Organization',
            '@id': `${seoDefaults.siteUrl}/#organization`,
            name: 'RAWS',
            url: `${seoDefaults.siteUrl}/`,
            email: 'halo@raws.app',
            logo: {
                '@type': 'ImageObject',
                url: buildAbsoluteUrl(seoDefaults.shareImagePath),
            },
            sameAs: [
                'https://paper.raws.app/',
                'https://www.youtube.com/@weareraws5728',
                'https://www.facebook.com/WeAreRaws/',
                'https://discord.gg/E8Tx38tsMq',
            ],
        },
        {
            '@type': 'SoftwareApplication',
            '@id': `${seoDefaults.siteUrl}/#application`,
            name: 'RAWS',
            applicationCategory: 'BusinessApplication',
            operatingSystem: 'Web',
            url: `${seoDefaults.siteUrl}/`,
            image: buildAbsoluteUrl(seoDefaults.shareImagePath),
            description: seoDefaults.description,
            inLanguage: 'pl-PL',
            publisher: {
                '@id': `${seoDefaults.siteUrl}/#organization`,
            },
            featureList: [
                'zarzadzanie sesjami fotograficznymi',
                'udostepnianie galerii klientom',
                'wybor zdjec przez klienta',
                'obsluga cennikow i zamowien',
                'pobieranie gotowych zdjec',
                'komunikacja z klientem',
            ],
        },
        {
            '@type': 'FAQPage',
            '@id': `${seoDefaults.siteUrl}/#faq`,
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'Czym jest RAWS?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'RAWS to webowa aplikacja dla fotografow do obslugi sesji, galerii online, wyboru zdjec, zamowien, cennikow i pobran.',
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Dla kogo jest RAWS?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'RAWS jest dla fotografow i studiow fotograficznych, ktore chca uporzadkowac obsluge klienta od publikacji galerii po finalizacje zamowien.',
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Jakie zadania wspiera RAWS?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'RAWS wspiera zarzadzanie sesjami, publikowanie galerii, selekcje zdjec, obsluge cennikow, komunikacje z klientem oraz dostarczanie gotowych plikow.',
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Gdzie znalezc publiczne informacje o RAWS?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'Podstawowe informacje znajduja sie na raws.app, a aktualnosci i materialy dodatkowe na blogu paper.raws.app. Publicznie dostepne sa tez strony regulaminu i polityki prywatnosci.',
                    },
                },
            ],
        },
    ],
});

const routeSeoConfig = (route: { name?: string | null; path?: string | null }): SeoConfig => {
    const routeName = route.name ? String(route.name) : null;

    switch (routeName) {
        case 'home':
            return {
                title: seoDefaults.title,
                description: seoDefaults.description,
                canonicalPath: '/',
                indexable: true,
                structuredData: createHomeSchema(),
            };
        case 'terms':
            return {
                title: 'Regulamin aplikacji RAWS',
                description: 'Regulamin korzystania z aplikacji RAWS.',
                canonicalPath: '/terms',
                indexable: true,
                structuredData: createGenericPageSchema('Regulamin aplikacji RAWS', 'Regulamin korzystania z aplikacji RAWS.', '/terms'),
            };
        case 'privacy':
            return {
                title: 'Polityka prywatności RAWS',
                description: 'Polityka prywatności aplikacji RAWS.',
                canonicalPath: '/privacy',
                indexable: true,
                structuredData: createGenericPageSchema('Polityka prywatności RAWS', 'Polityka prywatności aplikacji RAWS.', '/privacy'),
            };
        case 'confirmEmail':
            return {
                title: 'Potwierdzenie adresu e-mail | RAWS',
                description: 'Strona potwierdzenia adresu e-mail w aplikacji RAWS.',
                canonicalPath: route.path || '/confirm-email',
                indexable: false,
            };
        case 'recoverPassword':
            return {
                title: 'Reset hasła | RAWS',
                description: 'Strona resetowania hasła w aplikacji RAWS.',
                canonicalPath: route.path || '/recover-password',
                indexable: false,
            };
        case 'notFound':
            return {
                title: 'Nie znaleziono strony | RAWS',
                description: 'Nie znaleziono wskazanej strony w aplikacji RAWS.',
                canonicalPath: route.path || '/404',
                indexable: false,
            };
        default:
            return {
                title: 'Panel RAWS',
                description: 'Wewnętrzna część aplikacji RAWS dla fotografów.',
                canonicalPath: route.path || '/',
                indexable: false,
            };
    }
};

const setMetaTag = (selector: string, attrName: string, attrValue: string, content: string) => {
    let tag = document.querySelector(`meta[${selector}="${attrValue}"]`) as HTMLMetaElement | null;
    if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(attrName, attrValue);
        document.head.appendChild(tag);
    }
    tag.setAttribute('content', content);
};

const setCanonical = (href: string) => {
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', href);
};

const setStructuredData = (payload?: Record<string, unknown>) => {
    const scriptId = 'raws-structured-data';
    const existing = document.getElementById(scriptId);

    if (!payload) {
        if (existing) {
            existing.remove();
        }
        return;
    }

    const script = existing || document.createElement('script');
    script.id = scriptId;
    script.setAttribute('type', 'application/ld+json');
    script.textContent = JSON.stringify(payload);

    if (!existing) {
        document.head.appendChild(script);
    }
};

const applySeoForRoute = (route: { name?: string | null; path?: string | null }) => {
    const seo = routeSeoConfig(route);
    const canonicalPath = seo.canonicalPath || route.path || '/';
    const canonicalUrl = buildAbsoluteUrl(canonicalPath);
    const shareImageUrl = buildAbsoluteUrl(seoDefaults.shareImagePath);

    document.documentElement.setAttribute('lang', 'pl');
    document.title = seo.title;
    setCanonical(canonicalUrl);

    setMetaTag('name', 'name', 'description', seo.description);
    setMetaTag('name', 'name', 'robots', seo.indexable ? 'index,follow' : 'noindex,nofollow');
    setMetaTag('name', 'name', 'application-name', 'RAWS');

    setMetaTag('property', 'property', 'og:type', 'website');
    setMetaTag('property', 'property', 'og:site_name', 'RAWS');
    setMetaTag('property', 'property', 'og:locale', 'pl_PL');
    setMetaTag('property', 'property', 'og:title', seo.title);
    setMetaTag('property', 'property', 'og:description', seo.description);
    setMetaTag('property', 'property', 'og:url', canonicalUrl);
    setMetaTag('property', 'property', 'og:image', shareImageUrl);
    setMetaTag('property', 'property', 'og:image:secure_url', shareImageUrl);
    setMetaTag('property', 'property', 'og:image:alt', seoDefaults.shareImageAlt);
    setMetaTag('property', 'property', 'og:image:width', seoDefaults.shareImageWidth);
    setMetaTag('property', 'property', 'og:image:height', seoDefaults.shareImageHeight);

    setMetaTag('name', 'name', 'twitter:card', 'summary_large_image');
    setMetaTag('name', 'name', 'twitter:title', seo.title);
    setMetaTag('name', 'name', 'twitter:description', seo.description);
    setMetaTag('name', 'name', 'twitter:image', shareImageUrl);
    setMetaTag('name', 'name', 'twitter:image:alt', seoDefaults.shareImageAlt);

    setStructuredData(seo.structuredData);
};

const anonymousRoutes = [
    'home',
    'confirmEmail',
    'recoverPassword',
    'terms',
    'privacy',
];
const publicAnonymousRoutesForAuthenticated = [
    'terms',
    'privacy',
];
const loginStateResolver = (() => {
  let resolve = () => null;
  store.subscribe((mutation) => {
      if (mutation.type === 'setLoggedInAs') {
        resolve();
        resolve = () => null;
      }
  });

  return (newResolve: () => any) => { resolve = newResolve; };
})();

router.beforeEach(async (to, from, next) => {
    const userState = getModule(UserModule, store);
    if (userState.loginState === 'unknown') {
        userState.resolveLoginState();
        await new Promise<void>((resolve) => {
            loginStateResolver(resolve);
        });
    }

    if (userState.loginState === 'anonymous' && (!to.name || !anonymousRoutes.includes(to.name))) {
        userState.setRedirectRoute(to.fullPath);
        return next({ name: 'home' });
    }

    if (
      userState.loginState === 'authenticated' &&
      to.name &&
      anonymousRoutes.includes(to.name) &&
      !publicAnonymousRoutesForAuthenticated.includes(String(to.name))
    ) {
        return next(userState.redirectRoute);
    }

    return next();
});

router.afterEach((to) => {
    applySeoForRoute({
        name: to.name ? String(to.name) : null,
        path: to.path,
    });
});

applySeoForRoute({
    name: router.currentRoute.name ? String(router.currentRoute.name) : null,
    path: router.currentRoute.path,
});

export default router;
